<template>
  <TransitionRoot :show="open" as="template">
    <Dialog @close="closeModal" class="relative z-50">
      <TransitionChild
        as="template"
        enter="ease-out duration-300"
        enter-from="opacity-0"
        enter-to="opacity-100"
        leave="ease-in duration-200"
        leave-from="opacity-100"
        leave-to="opacity-0"
      >
        <div class="fixed inset-0 bg-gray-900/50 transition-opacity" />
      </TransitionChild>

      <div class="fixed inset-0 z-10 w-screen overflow-y-auto">
        <div class="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
          <TransitionChild
            as="template"
            enter="ease-out duration-300"
            enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            enter-to="opacity-100 translate-y-0 sm:scale-100"
            leave="ease-in duration-200"
            leave-from="opacity-100 translate-y-0 sm:scale-100"
            leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
          >
            <DialogPanel
              class="relative w-full max-w-3xl transform overflow-hidden rounded-2xl bg-white text-left shadow-2xl transition-all sm:my-8"
            >
              <LoadingOverlay :show="processingRequest">
                <!-- Header -->
                <div class="border-b border-gray-100 px-6 pt-6 pb-5">
                  <div class="flex items-start justify-between gap-4">
                    <!-- Only when there is one. An empty placeholder here would
                         indent the title of every product that has no picture
                         to make room for nothing. -->
                    <img
                      v-if="variant.image_url"
                      :src="variant.image_url"
                      :alt="variant.name || 'Product image'"
                      class="size-20 shrink-0 rounded-lg border border-gray-200 object-cover"
                    />
                    <div class="min-w-0">
                      <p class="text-xs font-medium uppercase tracking-wider text-gray-400">
                        Product
                      </p>
                      <DialogTitle class="mt-1 flex flex-wrap items-center gap-2 text-xl font-semibold text-gray-900">
                        <span class="truncate">{{ variant.name || '—' }}</span>
                        <!-- Company-wide only. A block that applies to some
                             branches and not others is not a fact about the
                             product, so it is said on the branch rows below
                             rather than here, where it would read as one. -->
                        <span
                          v-if="variant.not_for_sale"
                          class="inline-flex shrink-0 items-center rounded-full bg-rose-50 px-2.5 py-0.5 text-xs font-semibold text-rose-700 ring-1 ring-inset ring-rose-600/20"
                        >
                          Not for Sale
                        </span>
                      </DialogTitle>
                      <div class="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-gray-500">
                        <span v-if="variant.id" class="font-medium text-gray-700">PV-{{ variant.id }}</span>
                        <span v-if="variant.sku" class="text-gray-300">·</span>
                        <span v-if="variant.sku">SKU {{ variant.sku }}</span>
                        <span v-if="variant.code" class="text-gray-300">·</span>
                        <span v-if="variant.code">{{ variant.code }}</span>
                      </div>
                      <p v-if="variant.product_name" class="mt-2 text-sm text-gray-600">
                        {{ variant.product_name }}
                      </p>
                    </div>
                    <button
                      type="button"
                      @click="closeModal"
                      class="rounded-lg p-1.5 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600 focus:outline-none"
                    >
                      <XMarkIcon class="h-5 w-5" />
                    </button>
                  </div>

                  <!-- Summary metrics -->
                  <div class="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-4">
                    <div class="rounded-xl border border-gray-100 bg-gray-50 px-4 py-3">
                      <p class="text-xs font-medium text-gray-500">Total Stock</p>
                      <p class="mt-1 text-lg font-semibold text-gray-900">{{ variant.quantity ?? 0 }}</p>
                    </div>
                    <!-- What the customer is actually charged, at every branch.
                         Given the lead position because the catalogue figure
                         beside it is only a fallback: the POS reprices from the
                         latest receipt and quotes this, so this is the operative
                         number. One figure and not a per-branch one — the
                         product has a single sale price and the branch the goods
                         are sourced from does not move it. -->
                    <div class="rounded-xl border border-indigo-100 bg-indigo-50/60 px-4 py-3">
                      <p class="text-xs font-medium text-indigo-700">POS Sale Price</p>
                      <!-- Ex-tax leads because it is the figure the POS puts on
                           the line; the inclusive one is what the customer hands
                           over. Both are labelled rather than left to position,
                           so neither can be read as the other. -->
                      <p class="mt-1 text-lg font-semibold text-gray-900 tabular-nums">
                        {{ formatCurrency(posSalePrice) }}
                        <span class="text-xs font-normal text-gray-500">excl. tax</span>
                      </p>
                      <p v-if="posPriceHasTax" class="text-xs text-gray-500 tabular-nums">
                        {{ formatCurrency(posSalePriceInclusive) }}
                        <span class="text-gray-400">incl. tax</span>
                      </p>
                      <p class="mt-1 text-xs leading-snug text-gray-500">
                        <span class="whitespace-nowrap">{{ posPriceCaption.lead }}</span>
                        <span v-if="posPriceCaption.detail" class="block truncate text-gray-400">
                          {{ posPriceCaption.detail }}
                        </span>
                      </p>
                    </div>
                    <div class="rounded-xl border border-gray-100 bg-gray-50 px-4 py-3">
                      <p class="text-xs font-medium text-gray-500">Catalogue Price</p>
                      <p class="mt-1 text-lg font-semibold text-gray-900 tabular-nums">
                        {{ formatCurrency(variant.base_sales_price) }}
                        <span class="text-xs font-normal text-gray-500">excl. tax</span>
                      </p>
                      <p v-if="cataloguePriceHasTax" class="text-xs text-gray-500 tabular-nums">
                        {{ formatCurrency(variant.sales_price) }}
                        <span class="text-gray-400">incl. tax</span>
                      </p>
                      <!-- Named rather than left as "Sale Price": the POS only
                           falls back to this column when nothing has been
                           received, so calling it the sale price reads as a
                           promise the till does not keep. -->
                      <p v-if="catalogueIsStale" class="mt-1 text-xs leading-snug text-amber-600">
                        Not what the POS charges
                      </p>
                    </div>
                    <div v-if="canViewCost" class="rounded-xl border border-gray-100 bg-gray-50 px-4 py-3">
                      <p class="inline-flex items-center gap-1 text-xs font-medium text-gray-500">
                        Cost Price
                        <CostReveal toggle-only />
                      </p>
                      <CostReveal
                        :value="variant.cost_price"
                        :format="formatCurrency"
                        :show-toggle="false"
                        fallback="—"
                        value-class="mt-1 text-lg font-semibold text-gray-900"
                      />
                    </div>
                  </div>
                </div>

                <div class="max-h-[min(70vh,640px)] overflow-y-auto px-6 py-5">
                  <!-- Details -->
                  <section>
                    <h4 class="text-xs font-semibold uppercase tracking-wider text-gray-500">Details</h4>
                    <dl class="mt-3 grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-2">
                      <div v-for="field in detailFields" :key="field.label">
                        <dt class="text-xs text-gray-400">{{ field.label }}</dt>
                        <dd class="mt-0.5 text-sm font-medium text-gray-900">{{ field.value }}</dd>
                        <dd v-if="field.note" class="mt-0.5 text-xs leading-snug text-amber-600">
                          {{ field.note }}
                        </dd>
                      </div>
                    </dl>

                    <div v-if="variant.types?.length" class="mt-4">
                      <p class="text-xs text-gray-400">Types</p>
                      <ul class="mt-1.5 flex flex-wrap gap-1.5">
                        <li
                          v-for="t in variant.types"
                          :key="t.id"
                          class="rounded-md bg-indigo-50 px-2 py-0.5 text-xs font-medium text-indigo-700"
                        >
                          {{ t.text }}
                        </li>
                      </ul>
                    </div>

                    <div v-if="variant.description" class="mt-4">
                      <p class="text-xs text-gray-400">Description</p>
                      <p class="mt-1 whitespace-pre-line text-sm leading-relaxed text-gray-700">
                        {{ variant.description }}
                      </p>
                    </div>
                  </section>

                  <!-- Branch stock -->
                  <section class="mt-8">
                    <div class="flex flex-wrap items-center justify-between gap-3">
                      <h4 class="text-xs font-semibold uppercase tracking-wider text-gray-500">
                        Stock by Branch
                      </h4>
                      <span class="text-xs text-gray-400">
                        {{ inStockCount }} in stock · {{ outOfStockCount }} out · {{ unavailableCount }} unavailable
                      </span>
                    </div>

                    <div v-if="branchStocks.length" class="relative mt-3">
                      <MagnifyingGlassIcon
                        class="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400"
                      />
                      <input
                        v-model="branchSearch"
                        type="text"
                        placeholder="Search branch name..."
                        class="block w-full rounded-lg border border-gray-300 py-1.5 pl-8 pr-3 text-sm text-gray-900 placeholder:text-gray-400 focus:outline-2 focus:outline-indigo-500"
                      />
                    </div>

                    <div
                      v-if="!branchStocks.length"
                      class="mt-3 rounded-xl border border-dashed border-gray-200 px-4 py-8 text-center text-sm text-gray-500"
                    >
                      No branches found.
                    </div>

                    <div
                      v-else-if="!filteredBranchStocks.length"
                      class="mt-3 rounded-xl border border-dashed border-gray-200 px-4 py-8 text-center text-sm text-gray-500"
                    >
                      No branches match your search.
                    </div>

                    <div
                      v-else
                      class="mt-3 overflow-hidden rounded-xl border border-gray-200"
                    >
                      <ul class="divide-y divide-gray-100">
                        <li
                          v-for="row in filteredBranchStocks"
                          :key="row.branch_id"
                          class="flex items-center justify-between gap-3 px-4 py-3 transition-colors hover:bg-gray-50/80"
                        >
                          <div class="flex min-w-0 items-center gap-3">
                            <div
                              class="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg"
                              :class="branchIconClass(row.status)"
                            >
                              <BuildingStorefrontIcon class="h-4 w-4" />
                            </div>
                            <div class="min-w-0">
                              <p class="truncate text-sm font-medium text-gray-900">
                                {{ row.branch_name }}
                              </p>
                              <!-- Receiving history, not a price. Every branch
                                   sells at the one figure in the tile above; this
                                   says what this branch last stocked the goods at,
                                   which is how the operator sees where the current
                                   price came from. -->
                              <p class="mt-0.5 truncate text-xs text-gray-500">
                                {{ branchReceiptLabel(row) }}
                              </p>
                            </div>
                          </div>
                          <div class="flex shrink-0 items-center gap-3">
                            <!-- The branch's own switch, not the company one:
                                 with the company switch on every row would carry
                                 this and it would say nothing the title has not
                                 already said. -->
                            <span
                              v-if="row.not_for_sale_branch"
                              class="inline-flex items-center rounded-full bg-rose-50 px-2.5 py-0.5 text-xs font-medium text-rose-700 ring-1 ring-inset ring-rose-600/20"
                            >
                              Not for Sale
                            </span>
                            <span
                              class="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium"
                              :class="statusBadgeClass(row.status)"
                            >
                              {{ statusLabel(row.status) }}
                            </span>
                            <span
                              v-if="row.status !== 'unavailable'"
                              class="min-w-[3rem] text-right text-sm font-semibold tabular-nums"
                              :class="row.real_qty > 0 ? 'text-gray-900' : 'text-gray-400'"
                            >
                              {{ row.real_qty }}
                            </span>
                            <span v-else class="min-w-[3rem] text-right text-sm text-gray-400">—</span>
                          </div>
                        </li>
                      </ul>
                    </div>
                  </section>
                </div>

                <!-- Footer -->
                <div class="flex justify-end border-t border-gray-100 bg-gray-50 px-6 py-4">
                  <button
                    type="button"
                    @click="closeModal"
                    class="inline-flex w-full justify-center rounded-xl bg-white px-5 py-2.5 text-sm font-semibold text-gray-700 shadow-sm ring-1 ring-inset ring-gray-200 transition-colors hover:bg-gray-50 sm:w-auto"
                  >
                    Close
                  </button>
                </div>
              </LoadingOverlay>
            </DialogPanel>
          </TransitionChild>
        </div>
      </div>
    </Dialog>
  </TransitionRoot>
</template>

<script setup>
import { computed, ref, onMounted } from 'vue';
import { Dialog, TransitionChild, TransitionRoot, DialogTitle, DialogPanel } from '@headlessui/vue';
import { BuildingStorefrontIcon, MagnifyingGlassIcon, XMarkIcon } from '@heroicons/vue/24/outline';
import apiClient from '@/utils/axios.js';
import LoadingOverlay from '@/components/LoadingOverlay.vue';
import CostReveal from '@/components/CostReveal.vue';
import { toLocalDateString } from '@/utils/businessDate.js';
import { useAuthStore } from '@/stores/auth-store.js';

const props = defineProps({ variantId: [Number, String] });
const emits = defineEmits(['closeModal']);
const auth = useAuthStore();

const open = ref(false);
const processingRequest = ref(false);
const canViewCost = computed(() => auth.canViewCost());

const variant = ref({});
const branchStocks = ref([]);
const branchSearch = ref('');

/**
 * How the goods are taxed, in the order the figures above depend on it: the rate
 * the prices are net of, then the basis that rate stands on.
 *
 * An exempt or zero-rated supply reports that instead of a rate, because "0%"
 * and "outside the tax" are different facts and only one of them survives a
 * category being re-rated.
 *
 * Third schedule is called out because it moves the base rather than the rate:
 * the tax stands on the printed retail price, not on what the goods sell for, so
 * a discount does not shrink it.
 */
const taxCategorySummary = computed(() => {
  const t = variant.value.tax_category;
  if (!t) return null;

  const parts = [];
  if (t.supply_type === 'exempt') parts.push('Exempt');
  else if (t.supply_type === 'zero_rated') parts.push('Zero-rated');
  else parts.push(`GST ${Number(t.applied_tax_percentage ?? 0)}%`);

  if (t.third_schedule) parts.push('third schedule (taxed on retail price)');
  if (t.fed_excisable) parts.push('FED');

  return parts.join(' · ');
});

/**
 * The category has been re-rated since the goods were last received.
 *
 * Worth surfacing rather than quietly showing the frozen rate: the POS keeps
 * charging it until the next receipt, so the category screen and the till
 * genuinely disagree until then.
 */
const taxRateIsFrozen = computed(() => {
  const t = variant.value.tax_category;
  if (!t || t.supply_type !== 'standard') return false;
  return Number(t.applied_tax_percentage ?? 0) !== Number(t.tax_percentage ?? 0);
});

const detailFields = computed(() => {
  const v = variant.value;
  const fields = [
    { label: 'Tax Category', value: v.tax_category?.name },
    {
      label: 'Sales Tax',
      value: taxCategorySummary.value,
      note: taxRateIsFrozen.value
        ? `Category is now ${Number(v.tax_category?.tax_percentage ?? 0)}% — applies from the next receipt`
        : null,
    },
    { label: 'Model', value: v.model },
    { label: 'Brand', value: v.brand },
    { label: 'Manufacturer', value: v.manufacturer },
    { label: 'Material', value: v.material },
    { label: 'Color', value: v.color },
    { label: 'Size', value: v.size },
    { label: 'Weight', value: v.weight },
    { label: 'Length', value: v.length },
    { label: 'Width', value: v.width },
    { label: 'Height', value: v.height },
    { label: 'Diameter', value: v.diameter },
    { label: 'Thickness', value: v.thickness },
    { label: 'Volume', value: v.volume },
  ];

  return fields
    .filter((f) => f.value !== null && f.value !== undefined && f.value !== '')
    .map((f) => ({ ...f, value: String(f.value) }));
});

/**
 * The last receipt that actually set a price, or null if none ever did.
 *
 * The server already excludes rows that left the price alone, so a row arriving
 * here is a real decision; the zero guard only covers a payload predating the
 * field, which reads as "no receipt" rather than as free goods.
 */
const latestReceipt = computed(() => {
  const row = variant.value.latest_received_sale_price;
  return row && Number(row.sale_price) > 0 ? row : null;
});

/**
 * What the POS charges, resolved the way every selling surface resolves it: the
 * product's one sale price, set by the latest receipt anywhere and falling back
 * to the catalogue only when nothing has ever been received.
 *
 * The branch a sale is sourced from does not change it — the till, the exchange
 * and the wholesale screens all read the same unfiltered
 * LatestReceiving::pricesFor(), so this figure is the one that gets charged
 * wherever the goods come off the shelf.
 *
 * Shown net of sales tax. A shelf price is stored tax-inclusive and a sale line
 * is priced excluding tax, so this is what the POS actually puts on the line;
 * the tax is added back on top of it at checkout. The server strips it at the
 * rate the receipt froze — never recomputed here, because the rate a component
 * would have to guess at is exactly what the snapshot rule exists to settle.
 */
const posSalePrice = computed(() =>
  latestReceipt.value ? latestReceipt.value.base_sale_price : variant.value.base_sales_price
);

/**
 * The same price as it is stored and as the customer pays it — tax included.
 *
 * Resolved off the same source as the ex-tax figure above, never re-derived from
 * it: adding a rate back here would put a second, disagreeing implementation of
 * the tax split in a component, and it is the server that knows which rate the
 * receipt froze.
 */
const posSalePriceInclusive = computed(() =>
  latestReceipt.value ? latestReceipt.value.sale_price : variant.value.sales_price
);

/**
 * Whether there is a second figure worth printing.
 *
 * A zero-rated or exempt product has the same price on both bases, and showing
 * it twice under two different labels reads as a screen with a bug in it rather
 * than as the absence of tax.
 */
const posPriceHasTax = computed(
  () => Number(posSalePriceInclusive.value ?? 0) !== Number(posSalePrice.value ?? 0)
);
const cataloguePriceHasTax = computed(
  () => Number(variant.value.sales_price ?? 0) !== Number(variant.value.base_sales_price ?? 0)
);

/**
 * Where that figure came from, so a fallback is never read as a repricing.
 *
 * Split into two lines rather than one string because the date carries hyphens,
 * and a browser treats those as break opportunities — left to itself the tile
 * wrapped mid-date as "2026-07-" / "21".
 */
const posPriceCaption = computed(() => {
  const row = latestReceipt.value;
  if (!row) return { lead: 'From catalogue', detail: 'Not yet received' };

  return {
    lead: row.receiving_date ? `Last received ${toLocalDateString(row.receiving_date)}` : 'From last receiving',
    detail: row.branch_name || '',
  };
});

/**
 * Does the catalogue column disagree with what the till would ring up?
 *
 * Compared on the ex-tax figures, because those are the two the tiles show. On
 * the inclusive pair this flagged a difference the operator could not see: a
 * catalogue of 118 at 18% and a receipt of 125 at a frozen 25% are both 100 of
 * value, so the warning sat under two identical numbers.
 */
const catalogueIsStale = computed(() => {
  if (!latestReceipt.value) return false;
  return Number(variant.value.base_sales_price ?? 0) !== Number(latestReceipt.value.base_sale_price);
});

/**
 * What this branch last received the goods at, and when.
 *
 * History, not a price: every branch sells at the one figure in the tile above.
 * A branch that never received the goods says so rather than showing the
 * catalogue number, which would read as a price it had set.
 *
 * Ex-tax first, then the inclusive figure, matching the tiles. Stripped per
 * branch at that receipt's own frozen rate rather than at the current one, so a
 * branch stocked before a category rate change still reports the base it was
 * actually received at.
 *
 * The inclusive figure is dropped rather than repeated where the two are equal,
 * which is every zero-rated or exempt product — "100.00 excl. · 100.00 incl."
 * reads as a screen with a bug in it.
 */
const branchReceiptLabel = (row) => {
  const price = Number(row.last_received_price ?? 0);
  if (!(price > 0)) return 'Never received';

  const base = row.last_received_base_price ?? null;
  const date = row.last_received_date ? toLocalDateString(row.last_received_date) : '';
  const money =
    base !== null && Number(base) !== price
      ? `${formatCurrency(base)} excl. · ${formatCurrency(price)} incl.`
      : formatCurrency(base ?? price);

  return `Last received here ${money}${date ? ` · ${date}` : ''}`;
};

const filteredBranchStocks = computed(() => {
  const q = branchSearch.value.trim().toLowerCase();
  if (!q) return branchStocks.value;
  return branchStocks.value.filter((r) =>
    String(r.branch_name || '').toLowerCase().includes(q)
  );
});

const inStockCount = computed(() =>
  branchStocks.value.filter((r) => r.status === 'in_stock').length
);
const outOfStockCount = computed(() =>
  branchStocks.value.filter((r) => r.status === 'out_of_stock').length
);
const unavailableCount = computed(() =>
  branchStocks.value.filter((r) => r.status === 'unavailable').length
);

const formatCurrency = (value, currency = 'PKR') => {
  if (value === null || value === undefined || value === '') return '—';
  try {
    return new Intl.NumberFormat('en-PK', {
      style: 'currency',
      currency,
      minimumFractionDigits: 2,
    }).format(Number(value) || 0);
  } catch {
    return `${value} ${currency}`;
  }
};

const statusLabel = (status) => {
  if (status === 'in_stock') return 'In Stock';
  if (status === 'out_of_stock') return 'Out of Stock';
  return 'Not Available';
};

const statusBadgeClass = (status) => {
  if (status === 'in_stock') return 'bg-emerald-50 text-emerald-700 ring-1 ring-inset ring-emerald-600/20';
  if (status === 'out_of_stock') return 'bg-amber-50 text-amber-700 ring-1 ring-inset ring-amber-600/20';
  return 'bg-gray-100 text-gray-600 ring-1 ring-inset ring-gray-500/10';
};

const branchIconClass = (status) => {
  if (status === 'in_stock') return 'bg-emerald-50 text-emerald-600';
  if (status === 'out_of_stock') return 'bg-amber-50 text-amber-600';
  return 'bg-gray-100 text-gray-400';
};

const closeModal = () => {
  open.value = false;
  emits('closeModal');
};

const fetchProductVariant = async () => {
  if (!props.variantId) return;

  try {
    processingRequest.value = true;

    const res = await apiClient.post(
      '/productVariant/fetchProductVariant',
      { id: props.variantId }
    );

    const v = res.data.data;

    branchStocks.value = Array.isArray(v.branch_stocks) ? v.branch_stocks : [];

    variant.value = {
      id: v.id,
      // This mapping is an allowlist, so a new server field is invisible here
      // until it is named — which is exactly how the picture went missing.
      image_url: v.image_url ?? null,
      product_name: v.product?.name || '-',
      name: v.name || '-',
      sku: v.sku || '',
      code: v.code || '',
      model: v.model || '',
      brand: v.brand || '',
      manufacturer: v.manufacturer || '',
      material: v.material?.name || null,
      color: v.color?.name || null,
      size: v.size?.name || null,
      weight: v.weight ?? null,
      length: v.length ?? null,
      width: v.width ?? null,
      height: v.height ?? null,
      diameter: v.diameter ?? null,
      thickness: v.thickness ?? null,
      volume: v.volume ?? null,
      description: v.description || '',
      types: v.types?.map((t) => ({ id: t.id, text: t.name })) || [],
      quantity: Number(v.total_real_qty ?? v.real_quantity ?? v.quantity ?? 0),
      sales_price: v.sales_price ?? null,
      // The catalogue figure net of tax, for the ex-tax tile when nothing has
      // ever been received. Null on a payload predating the field, which reads
      // as "no price" rather than as a zero-rated one.
      base_sales_price: v.base_sales_price ?? null,
      tax_category: v.tax_category ?? null,
      // Company-wide only. The per-branch answer rides on each branch_stocks
      // row, which is where a partial block belongs.
      not_for_sale: Boolean(v.not_for_sale),
      cost_price: canViewCost.value ? (v.cost_price ?? null) : null,
      latest_received_sale_price: v.latest_received_sale_price ?? null,
    };
  } catch (err) {
    console.error('Error fetching product variant:', err);
  } finally {
    processingRequest.value = false;
  }
};

onMounted(() => {
  open.value = true;
  fetchProductVariant();
});
</script>

<style scoped></style>
